import { isDevMode } from '@angular/core';
import type { AuroraWidgetDef } from './aurora-widget-def';

/**
 * Pure function: maps a widget definition array + session permission state →
 * the subset visible to the current account.
 *
 * Mirrors `filterNavItems` semantics for the widget domain (design D1, D3):
 *
 * ```
 * visible(widget) =
 *   widget.permission === undefined  → always visible
 *   permission === []                → not gated (treated as undefined; dev warning emitted)
 *   permissionsLoaded === false      → hidden (readiness gate)
 *   sessionPermissions.includes(widget.permission) → visible, else hidden
 * ```
 *
 * This function is pure and composable — the original array is never mutated.
 *
 * @param widgets - Full widget definition list from the registry.
 * @param sessionPermissions - Flat permission strings the current account holds.
 * @param permissionsLoaded - Whether the permission load has settled. Pass `false`
 *   to suppress all permission-gated widgets during the async post-login window.
 * @returns New array containing only the widgets the session may see.
 *
 * @example
 * ```typescript
 * const visible = computed(() =>
 *   filterWidgets(
 *     inject(AURORA_WIDGET, { optional: true }) ?? [],
 *     currentAccount.permissions(),
 *     currentAccount.permissionsLoaded(),
 *   )
 * );
 * ```
 */
export function filterWidgets(
  widgets: AuroraWidgetDef[],
  sessionPermissions: readonly string[],
  permissionsLoaded: boolean,
): AuroraWidgetDef[] {
  return widgets.filter((widget) => isWidgetVisible(widget, sessionPermissions, permissionsLoaded));
}

function isWidgetVisible(
  widget: AuroraWidgetDef,
  sessionPermissions: readonly string[],
  permissionsLoaded: boolean,
): boolean {
  const isPermissionGated = widget.permission !== undefined;

  if (!isPermissionGated) {
    return true;
  }

  // Array permission: require ALL (design D5). Single string: backward-compatible check.
  // At this point `widget.permission` is defined (isPermissionGated guard above).
  const permission = widget.permission as string | string[];

  // An empty require-all list is vacuously true but likely a misconfiguration.
  // Treat it as not-gated (visible to everyone, bypasses the readiness gate too)
  // and emit a dev-only warning so the author knows to use `permission: undefined`
  // to suppress it (C2 fix).
  if (Array.isArray(permission) && permission.length === 0) {
    if (isDevMode()) {
      console.warn(
        `[aurora-widget] Widget "${widget.id}" has permission: [] (empty array). ` +
          `An empty require-all list does not restrict visibility — the widget is shown to everyone. ` +
          `Use permission: undefined to suppress this warning.`,
      );
    }
    return true;
  }

  if (!permissionsLoaded) {
    return false;
  }

  if (Array.isArray(permission)) {
    return permission.every((perm) => sessionPermissions.includes(perm));
  }

  return sessionPermissions.includes(permission);
}
